import React, { useState, useCallback, useRef } from 'react';
import { Upload, Download, AlertCircle, FileCode, RotateCcw, Copy, Check, Lock, Unlock, Loader2 } from 'lucide-react';

type InputMode = 'file' | 'paste';
type OutputFormat = 'png' | 'jpeg';

const SCALES = [1, 2, 3, 4] as const;

const SvgToPng: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [svgCode, setSvgCode] = useState('');
  const [svgPreviewUrl, setSvgPreviewUrl] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [autoWidth, setAutoWidth] = useState(0);
  const [autoHeight, setAutoHeight] = useState(0);
  const [scale, setScale] = useState(1);
  const [lockAspect, setLockAspect] = useState(true);
  const [bgOption, setBgOption] = useState<'transparent' | 'white' | 'custom'>('transparent');
  const [customBg, setCustomBg] = useState('#ffffff');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpegQuality, setJpegQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<InputMode>('file');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const parseSvgDimensions = (svgText: string): { w: number; h: number } => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      setValidationErrors(['SVG parsing error: ' + (parseError.textContent?.substring(0, 100) || 'Invalid SVG')]);
    } else {
      setValidationErrors([]);
    }

    const svgEl = doc.querySelector('svg');
    if (!svgEl) return { w: 300, h: 150 };

    const viewBox = svgEl.getAttribute('viewBox');
    const wAttr = svgEl.getAttribute('width');
    const hAttr = svgEl.getAttribute('height');

    if (wAttr && hAttr) {
      return { w: parseInt(wAttr) || 300, h: parseInt(hAttr) || 150 };
    }
    if (viewBox) {
      const parts = viewBox.split(/[\s,]+/);
      if (parts.length === 4) {
        return { w: parseFloat(parts[2]) || 300, h: parseFloat(parts[3]) || 150 };
      }
    }
    return { w: 300, h: 150 };
  };

  const loadSvg = useCallback((svgText: string) => {
    if (!svgText.includes('<svg')) {
      setError('Invalid SVG code. Must contain an <svg> element.');
      setValidationErrors(['No <svg> element found in the provided code.']);
      return;
    }

    const { w, h } = parseSvgDimensions(svgText);
    setAutoWidth(w);
    setAutoHeight(h);
    setWidth(w);
    setHeight(h);
    setScale(1);
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    setSvgPreviewUrl(URL.createObjectURL(blob));
    setSvgCode(svgText);
    setResultUrl(null);
    setResultBlob(null);
    setError(null);
  }, []);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.name.endsWith('.svg') && f.type !== 'image/svg+xml') {
      setError('Please select an SVG file.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      loadSvg(text);
    };
    reader.readAsText(f);
  }, [loadSvg]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handlePaste = () => {
    if (!svgCode.trim()) {
      setError('Please paste SVG code.');
      return;
    }
    loadSvg(svgCode);
  };

  const handleWidthChange = (newW: number) => {
    setWidth(newW);
    if (lockAspect && autoWidth > 0) {
      setHeight(Math.round((newW / autoWidth) * autoHeight));
    }
  };

  const handleHeightChange = (newH: number) => {
    setHeight(newH);
    if (lockAspect && autoHeight > 0) {
      setWidth(Math.round((newH / autoHeight) * autoWidth));
    }
  };

  const handleScaleClick = (s: number) => {
    setScale(s);
    setWidth(autoWidth * s);
    setHeight(autoHeight * s);
  };

  const convertToRaster = useCallback(() => {
    if (!svgCode) return;
    setIsProcessing(true);
    setError(null);

    const svgBlob = new Blob([svgCode], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new window.Image();

    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const w = width || autoWidth;
      const h = height || autoHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Background
      if (bgOption === 'white') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
      } else if (bgOption === 'custom') {
        ctx.fillStyle = customBg;
        ctx.fillRect(0, 0, w, h);
      } else {
        ctx.clearRect(0, 0, w, h);
      }

      ctx.drawImage(img, 0, 0, w, h);

      const mimeType = outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png';
      const quality = outputFormat === 'jpeg' ? jpegQuality / 100 : undefined;

      canvas.toBlob((blob) => {
        if (blob) {
          setResultUrl(URL.createObjectURL(blob));
          setResultBlob(blob);
        }
        setIsProcessing(false);
        URL.revokeObjectURL(url);
      }, mimeType, quality);
    };

    img.onerror = () => {
      setError('Failed to render SVG. Check that the SVG code is valid.');
      setIsProcessing(false);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  }, [svgCode, width, height, autoWidth, autoHeight, bgOption, customBg, outputFormat, jpegQuality]);

  const downloadResult = () => {
    if (!resultUrl) return;
    const link = document.createElement('a');
    link.href = resultUrl;
    const name = file ? file.name.replace(/\.[^.]+$/, '') : 'svg_converted';
    const ext = outputFormat === 'jpeg' ? 'jpg' : 'png';
    link.download = `${name}_${width}x${height}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async () => {
    if (!resultBlob) return;
    try {
      // For PNG, use clipboard API
      if (outputFormat === 'png') {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': resultBlob }),
        ]);
      } else {
        // For JPEG, convert to PNG first for clipboard
        const canvas = canvasRef.current;
        if (!canvas) return;
        const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (pngBlob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': pngBlob }),
          ]);
        }
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      setError('Failed to copy to clipboard. This feature may not be supported in your browser.');
    }
  };

  const reset = () => {
    setFile(null);
    setSvgCode('');
    setSvgPreviewUrl(null);
    setResultUrl(null);
    setResultBlob(null);
    setWidth(0);
    setHeight(0);
    setScale(1);
    setError(null);
    setValidationErrors([]);
    setBgOption('transparent');
    setOutputFormat('png');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasSvg = !!svgPreviewUrl;

  const checkerboardStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
  };

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-lime-100 rounded-full">
            <FileCode className="w-8 h-8 text-lime-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">SVG to PNG/JPEG Converter</h2>
        <p className="text-gray-600">
          Convert SVG files or code to raster images with custom scale and background.
        </p>
      </div>

      {/* Mode Toggle */}
      {!hasSvg && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setMode('file')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              mode === 'file' ? 'bg-lime-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Upload File
          </button>
          <button
            onClick={() => setMode('paste')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              mode === 'paste' ? 'bg-lime-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileCode className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Paste Code
          </button>
        </div>
      )}

      {/* Upload / Paste */}
      {!hasSvg && mode === 'file' && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-lime-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an SVG file here or click to select
          </h3>
          <input
            ref={fileInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {!hasSvg && mode === 'paste' && (
        <div className="space-y-3">
          <textarea
            value={svgCode}
            onChange={(e) => setSvgCode(e.target.value)}
            placeholder='Paste your SVG code here, e.g. <svg viewBox="0 0 100 100">...</svg>'
            className="w-full h-48 p-4 bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-lime-500 font-mono text-sm"
          />
          <button
            onClick={handlePaste}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-lime-600 text-white font-medium rounded-xl hover:bg-lime-700 transition-colors"
          >
            <FileCode className="w-5 h-5" />
            Load SVG
          </button>
        </div>
      )}

      {/* Errors */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-800 font-medium text-sm">SVG Validation Warnings</p>
              {validationErrors.map((err, i) => (
                <p key={i} className="text-amber-700 text-sm mt-1">{err}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasSvg && (
        <div className="space-y-4">
          {/* Scale Buttons */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="text-sm font-medium text-gray-700 mb-3 block">Scale</label>
            <div className="flex gap-2 mb-4">
              {SCALES.map((s) => (
                <button
                  key={s}
                  onClick={() => handleScaleClick(s)}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    scale === s
                      ? 'bg-lime-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {s}x
                  {s > 1 && <span className="block text-xs opacity-75">Retina</span>}
                </button>
              ))}
            </div>

            {/* Custom Dimensions */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Width (px)</label>
                <input
                  type="number"
                  value={width}
                  onChange={(e) => handleWidthChange(parseInt(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lime-500 text-sm"
                />
              </div>
              <button
                onClick={() => setLockAspect(!lockAspect)}
                className={`p-2 rounded-lg mb-0.5 transition-colors ${
                  lockAspect ? 'text-lime-600 bg-lime-50' : 'text-gray-400 hover:text-gray-600'
                }`}
                title={lockAspect ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
              >
                {lockAspect ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </button>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Height (px)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => handleHeightChange(parseInt(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lime-500 text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Detected: {autoWidth} x {autoHeight}px
            </p>
          </div>

          {/* Background + Format */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Background</label>
              <div className="flex gap-1">
                {(['transparent', 'white', 'custom'] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setBgOption(opt)}
                    className={`flex-1 px-2 py-1.5 rounded text-xs font-medium capitalize transition-colors ${
                      bgOption === opt
                        ? 'bg-lime-600 text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {bgOption === 'custom' && (
                <input
                  type="color"
                  value={customBg}
                  onChange={(e) => setCustomBg(e.target.value)}
                  className="w-full h-8 rounded-lg cursor-pointer border border-gray-300 mt-2"
                />
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Output Format</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setOutputFormat('png')}
                  className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    outputFormat === 'png'
                      ? 'bg-lime-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  PNG
                </button>
                <button
                  onClick={() => setOutputFormat('jpeg')}
                  className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    outputFormat === 'jpeg'
                      ? 'bg-lime-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  JPEG
                </button>
              </div>
              {outputFormat === 'jpeg' && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Quality</span>
                    <span className="font-bold text-lime-600">{jpegQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={jpegQuality}
                    onChange={(e) => setJpegQuality(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-lime-600"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Previews */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">SVG Preview</h3>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px] p-4">
                {svgPreviewUrl && (
                  <img src={svgPreviewUrl} alt="SVG Preview" className="max-w-full max-h-[400px] object-contain" />
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {outputFormat.toUpperCase()} Result
              </h3>
              <div
                className="rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]"
                style={bgOption === 'transparent' ? checkerboardStyle : { backgroundColor: '#f3f4f6' }}
              >
                {resultUrl ? (
                  <img src={resultUrl} alt="Result" className="max-w-full max-h-[400px] object-contain" />
                ) : (
                  <span className="text-gray-400 py-12">Click &quot;Convert&quot; to generate</span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={convertToRaster}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-lime-600 text-white font-medium rounded-xl hover:bg-lime-700 disabled:opacity-50 transition-colors"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <FileCode className="w-5 h-5" />
                  Convert to {outputFormat.toUpperCase()}
                </>
              )}
            </button>
            {resultUrl && (
              <>
                <button
                  onClick={downloadResult}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Download
                </button>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </>
            )}
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SvgToPng;
